import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Flag, ChevronLeft, ChevronRight, Pause, Play, AlertTriangle, CheckCircle2, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

export default function PracticeEngine() {
  const { courseId, testNumber } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "practice"; // practice | timed | review
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> selected
  const [marked, setMarked] = useState(new Set());
  const [paused, setPaused] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const startedAtRef = useRef(Date.now());

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch {
      /* fullscreen blocked */
    }
  };

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Auto-save attempt every 15s when there are unsaved changes
  useEffect(() => {
    if (!attempt) return;
    const t = setInterval(() => {
      api.patch(`/attempts/${attempt.id}/progress`, {
        answers,
        marked_for_review: Array.from(marked),
        time_taken_seconds: Math.floor((Date.now() - startedAtRef.current) / 1000),
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [attempt, answers, marked]);

  useEffect(() => {
    api.post("/attempts/start", {
      course_id: courseId,
      test_number: parseInt(testNumber, 10),
      mode,
    }).then(({ data }) => {
      setAttempt(data.attempt);
      setQuestions(data.questions);
      setAnswers(data.attempt.answers || {});
      setMarked(new Set(data.attempt.marked_for_review || []));
      startedAtRef.current = Date.now() - ((data.attempt.time_taken_seconds || 0) * 1000);
      if (mode === "timed") {
        setSecondsLeft(Math.max(0, ((data.attempt.duration_minutes || 130) * 60) - (data.attempt.time_taken_seconds || 0)));
      }
      if (data.resumed) {
        toast.success("Resumed your saved attempt");
      }
    }).catch((e) => {
      toast.error(formatErr(e.response?.data?.detail) || "Could not start attempt");
      const slug = e.response?.data?.detail?.course_slug;
      navigate(slug ? `/courses/${slug}` : "/dashboard");
    });
  }, [courseId, testNumber, mode, navigate]);

  // Timer
  useEffect(() => {
    if (mode !== "timed" || paused || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [mode, paused, secondsLeft]);

  useEffect(() => {
    if (mode === "timed" && secondsLeft === 0 && questions.length > 0 && attempt && !submitting) {
      finish();
    }
    // eslint-disable-next-line
  }, [secondsLeft]);

  const current = questions[idx];

  const selectAnswer = (qid, i) => {
    setAnswers((a) => ({ ...a, [qid]: i }));
  };
  const toggleMark = (qid) => {
    setMarked((m) => {
      const n = new Set(m);
      n.has(qid) ? n.delete(qid) : n.add(qid);
      return n;
    });
  };

  const formatTime = (s) => {
    if (s < 0) s = 0;
    const m = Math.floor(s / 60), sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const answeredCount = Object.keys(answers).length;

  const finish = async () => {
    if (!attempt || submitting) return;
    setSubmitting(true);
    const time_taken = Math.floor((Date.now() - startedAtRef.current) / 1000);
    try {
      const { data } = await api.post("/attempts/submit", {
        attempt_id: attempt.id,
        answers,
        marked_for_review: Array.from(marked),
        time_taken_seconds: time_taken,
      });
      navigate(`/results/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not submit attempt");
      setSubmitting(false);
    }
  };

  if (!attempt || !current) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="practice-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--brand-cream-2)]" data-testid="practice-engine">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="container-x flex items-center gap-4 h-14">
          <div className="font-display text-base">
            {mode === "mistakes" ? "Mistake drill" : "Mock test"} <span className="text-muted-foreground">#{testNumber}</span>
          </div>
          <div className="text-xs text-muted-foreground hidden sm:block">{mode.toUpperCase()} mode</div>

          <div className="ml-auto flex items-center gap-3">
            <button onClick={toggleFullscreen} className="rounded-md border border-border h-8 px-2 text-xs font-semibold inline-flex items-center gap-1 hover:bg-accent" data-testid="fullscreen-toggle" aria-label="Toggle fullscreen">
              {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{fullscreen ? "Exit" : "Fullscreen"}</span>
            </button>
            {mode === "timed" && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPaused(!paused)} className="rounded-md border border-border h-8 px-2 text-xs font-semibold inline-flex items-center gap-1" data-testid="pause-button">
                  {paused ? <><Play className="h-3.5 w-3.5" /> Resume</> : <><Pause className="h-3.5 w-3.5" /> Pause</>}
                </button>
                <div className={`font-mono font-bold tabular-nums ${secondsLeft < 300 ? "text-destructive" : ""}`} data-testid="timer">
                  {formatTime(secondsLeft)}
                </div>
              </div>
            )}
            <div className="text-xs text-muted-foreground">{answeredCount}/{questions.length} answered</div>
            <Button onClick={finish} disabled={submitting} className="h-9 bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white" data-testid="submit-button">
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </div>
      </header>

      <div className="container-x py-6 grid lg:grid-cols-[1fr_280px] gap-6">
        {/* Question */}
        <main>
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase font-bold tracking-[0.18em] text-muted-foreground">
                Question {idx + 1} of {questions.length}
              </div>
              <button
                onClick={() => toggleMark(current.id)}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold ${
                  marked.has(current.id) ? "bg-yellow-400 text-yellow-950" : "border border-border hover:bg-accent"
                }`}
                data-testid="mark-review-button"
              >
                <Flag className="h-3.5 w-3.5" /> {marked.has(current.id) ? "Marked" : "Mark for review"}
              </button>
            </div>
            <h2 className="font-display text-xl sm:text-2xl tracking-tight leading-snug">{current.text}</h2>
            <div className="mt-6 space-y-3">
              {current.options.map((opt, i) => {
                const selected = answers[current.id] === i;
                return (
                  <button
                    key={i}
                    onClick={() => selectAnswer(current.id, i)}
                    className={`w-full text-left rounded-lg border p-4 transition-all ${
                      selected
                        ? "border-[color:var(--brand-blue)] bg-[color:var(--brand-blue)]/5"
                        : "border-border hover:bg-accent"
                    }`}
                    data-testid={`option-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-6 w-6 shrink-0 rounded-full border-2 grid place-items-center text-xs font-bold ${
                        selected ? "border-[color:var(--brand-blue)] bg-[color:var(--brand-blue)] text-white" : "border-border"
                      }`}>
                        {String.fromCharCode(65 + i)}
                      </div>
                      <span className="pt-0.5">{opt}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setIdx(Math.max(0, idx - 1))}
                disabled={idx === 0}
                className="inline-flex items-center gap-1.5 rounded-md border border-border h-10 px-4 text-sm font-semibold disabled:opacity-50 hover:bg-accent"
                data-testid="prev-question"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <button
                onClick={() => setIdx(Math.min(questions.length - 1, idx + 1))}
                disabled={idx === questions.length - 1}
                className="inline-flex items-center gap-1.5 rounded-md bg-foreground text-background h-10 px-4 text-sm font-semibold disabled:opacity-50"
                data-testid="next-question"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {mode === "mistakes" && (
            <div className="mt-4 rounded-xl border border-amber-400/30 p-4 bg-amber-400/10 text-sm text-muted-foreground flex items-start gap-2" data-testid="mistake-drill-note">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--brand-success)]" />
              <span>Answer these previously missed questions correctly to clear them from your mistake bank.</span>
            </div>
          )}

          {mode === "practice" && answers[current.id] !== undefined && (
            <div className="mt-4 rounded-xl border border-border p-4 bg-card text-sm text-muted-foreground flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--brand-success)]" />
              <span>In <b>practice mode</b>, full explanations appear in your results page after submitting.</span>
            </div>
          )}
        </main>

        {/* Question palette */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs uppercase font-bold tracking-[0.18em] text-muted-foreground mb-3">Question palette</div>
            <div className="grid grid-cols-8 lg:grid-cols-7 gap-1.5">
              {questions.map((q, i) => {
                const isAnswered = answers[q.id] !== undefined;
                const isMarked = marked.has(q.id);
                const isCurrent = i === idx;
                let cls = "unanswered";
                if (isMarked) cls = "marked";
                else if (isAnswered) cls = "answered";
                return (
                  <button
                    key={q.id}
                    onClick={() => setIdx(i)}
                    className={`palette-btn ${cls} ${isCurrent ? "current" : ""}`}
                    data-testid={`palette-${i}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><span className="palette-btn answered h-4 w-4">·</span> Answered</div>
              <div className="flex items-center gap-2"><span className="palette-btn marked h-4 w-4">·</span> Marked</div>
              <div className="flex items-center gap-2"><span className="palette-btn unanswered h-4 w-4">·</span> Unanswered</div>
            </div>
            {mode === "timed" && secondsLeft < 300 && secondsLeft > 0 && (
              <div className="mt-4 rounded-md border border-yellow-400/30 bg-yellow-400/10 p-3 text-xs flex gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 mt-0.5" />
                Less than 5 minutes left
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
